import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { AuthSettings, AuthAllowlistMode } from '@/lib/types';

/** Read the current auth settings (allowed email domains, audit metadata). */
export function useAuthSettings() {
  return useQuery<AuthSettings>({
    queryKey: queryKeys.authSettings,
    queryFn: () => api.getAuthSettings(),
    staleTime: 30_000,
  });
}

export interface UpdateAuthSettingsArgs {
  allowedEmailDomains: string[];
  allowedEmails: string[];
  mode: AuthAllowlistMode;
  /**
   * When provided, updates whether ordinary users can create their own
   * organizations. Omit to preserve the currently-saved value.
   */
  allowSelfServeOrgCreation?: boolean;
}

/**
 * Replace the allowlist with a new domain set + per-email set + mode.
 * The server returns the canonical (lower-cased, deduped) lists which
 * are written straight into the cache so the UI updates without a refetch.
 */
export function useUpdateAuthSettings() {
  const queryClient = useQueryClient();
  return useMutation<AuthSettings, Error, UpdateAuthSettingsArgs>({
    mutationFn: ({ allowedEmailDomains, allowedEmails, mode, allowSelfServeOrgCreation }) =>
      api.updateAuthSettings(allowedEmailDomains, mode, allowedEmails, allowSelfServeOrgCreation),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.authSettings, data);
    },
  });
}
